// Catálogo editorial de páginas SEO. Adicionar/remover entradas aqui controla
// quais URLs são geradas e indexadas. Mantemos isso em código (não no DB) para
// evitar páginas geradas automaticamente sem revisão e fica fácil versionar.

export type SeoKind = "modelo" | "marca";

export type SeoEntry = {
  slug: string; // segmento de URL: "volvo-xc90"
  displayName: string; // "Volvo XC90"
  brand: string; // "Volvo"
  // Fallback ILIKE: usado quando `family` não está preenchido (páginas de marca,
  // toyota-sw4 — cuja base_family se mistura com Hilux picape). Mantenha curto.
  match: string[];
  // Caminho rápido: valor (ou array) da coluna `base_family` ou `model_slug` no DB.
  // Tanto o /buscar quanto o /blindados/* filtram por
  // `base_family.eq.X OR model_slug.eq.X` (igualdade indexada, ordens de
  // magnitude mais rápido que ILIKE em title/display_title).
  //   - String ("toyota-corolla-cross"): cobre todos os trims.
  //   - Array (["volvo-xc60", "volvo-xc40"]): agrega famílias quando
  //     editorialmente faz sentido. Em geral 1 string basta — o matcher já
  //     agrega trims sob a base_family principal.
  // Para famílias muito amplas (ex.: "volvo-xc" mistura XC40/60/90) deixe
  // vazio — cai no ILIKE com `match[]`.
  family?: string | string[];
  // Quando preenchido: filtra por coluna `location` em vez de ILIKE nos títulos (usado em estado pages).
  location?: string;
  // SEO copy hints, opcionais — usados no bloco de texto.
  shortDescription?: string;
};

// Ordem importante: os 12 primeiros aparecem como "mais procurados" na home
// e no discovery hub do /buscar. Ranking editorial baseado no volume real em
// prod (jun/2026, snapshot de ~6k ativos). `family` aponta para o valor de
// `base_family`/`model_slug` em `listings` — validado em prod antes de subir.
export const MODELOS: SeoEntry[] = [
  // ── Top 12 (em destaque por volume — ordem reflecte anúncios ativos em prod) ──
  {
    slug: "jeep-compass",
    displayName: "Jeep Compass",
    brand: "Jeep",
    match: ["compass"],
    family: "jeep-compass",
  },
  {
    slug: "toyota-corolla",
    displayName: "Toyota Corolla",
    brand: "Toyota",
    match: ["corolla"],
    family: "toyota-corolla",
  },
  {
    slug: "jeep-commander",
    displayName: "Jeep Commander",
    brand: "Jeep",
    match: ["commander"],
    family: "jeep-commander",
  },
  {
    slug: "toyota-hilux",
    displayName: "Toyota Hilux",
    brand: "Toyota",
    match: ["hilux"],
    family: "toyota-hilux",
  },
  {
    slug: "bmw-320i",
    displayName: "BMW 320i",
    brand: "BMW",
    match: ["320i"],
    family: "bmw-320i",
    shortDescription: "Sedã esportivo premium, um dos blindados mais procurados da categoria.",
  },
  {
    slug: "toyota-corolla-cross",
    displayName: "Toyota Corolla Cross",
    brand: "Toyota",
    match: ["corolla cross"],
    family: "toyota-corolla-cross",
  },
  {
    slug: "vw-tiguan",
    displayName: "Volkswagen Tiguan",
    brand: "Volkswagen",
    match: ["tiguan"],
    family: "volkswagen-tiguan",
  },
  {
    slug: "land-rover-discovery-sport",
    displayName: "Land Rover Discovery Sport",
    brand: "Land Rover",
    match: ["discovery sport"],
    family: "land-rover-discovery-sport",
  },
  {
    slug: "bmw-x1",
    displayName: "BMW X1",
    brand: "BMW",
    match: ["x1"],
    family: "bmw-x1",
  },
  {
    slug: "audi-q5",
    displayName: "Audi Q5",
    brand: "Audi",
    match: ["q5"],
    family: "audi-q5",
  },
  {
    slug: "land-rover-range-rover",
    displayName: "Land Rover Range Rover",
    brand: "Land Rover",
    match: ["range rover"],
    family: "land-rover-range-rover",
    shortDescription: "SUV premium britânico — Evoque, Velar e Sport lideram o mercado de blindados.",
  },
  {
    slug: "volvo-xc60",
    displayName: "Volvo XC60",
    brand: "Volvo",
    match: ["xc60", "xc 60"],
    family: "volvo-xc-60",
  },
  // ── Demais (alimentam SEO pages, autocomplete e o restante da exploração) ──
  {
    slug: "honda-hr-v",
    displayName: "Honda HR-V",
    brand: "Honda",
    match: ["hr-v", "hrv", "hr v"],
    family: "honda-hr",
  },
  {
    slug: "vw-t-cross",
    displayName: "Volkswagen T-Cross",
    brand: "Volkswagen",
    match: ["t-cross", "tcross", "t cross"],
    family: "volkswagen-t-cross",
  },
  {
    slug: "volvo-xc90",
    displayName: "Volvo XC90",
    brand: "Volvo",
    match: ["xc90", "xc 90"],
    family: "volvo-xc-90",
    shortDescription: "SUV grande premium com fama de durabilidade e ótima base para blindagem.",
  },
  {
    // No `family`: SW4 e Hilux picape compartilham base_family=toyota-hilux,
    // então igualdade não consegue distinguir os dois. Fica no ILIKE até a
    // matcher passar a popular model_slug com prefixo "toyota-hilux-sw4-*".
    slug: "toyota-sw4",
    displayName: "Toyota SW4",
    brand: "Toyota",
    match: ["sw4", "sw 4", "hilux sw"],
    shortDescription: "SUV grande com chassi de picape, alta liquidez no mercado de blindados.",
  },
  {
    slug: "vw-taos",
    displayName: "Volkswagen Taos",
    brand: "Volkswagen",
    match: ["taos"],
    family: "volkswagen-taos",
  },
  {
    slug: "vw-nivus",
    displayName: "Volkswagen Nivus",
    brand: "Volkswagen",
    match: ["nivus"],
    family: "volkswagen-nivus",
  },
  {
    slug: "honda-civic",
    displayName: "Honda Civic",
    brand: "Honda",
    match: ["civic"],
    family: "honda-civic",
  },
  {
    slug: "audi-q3",
    displayName: "Audi Q3",
    brand: "Audi",
    match: ["q3"],
    family: "audi-q3",
  },
  {
    slug: "mercedes-glc",
    displayName: "Mercedes-Benz GLC",
    brand: "Mercedes-Benz",
    match: ["glc"],
    family: "mercedes-benz-glc",
  },
  {
    slug: "mercedes-gle",
    displayName: "Mercedes-Benz GLE",
    brand: "Mercedes-Benz",
    match: ["gle"],
    family: "mercedes-benz-gle",
  },
  {
    slug: "chevrolet-equinox",
    displayName: "Chevrolet Equinox",
    brand: "Chevrolet",
    match: ["equinox"],
    family: "gm-chevrolet-equinox",
  },
  {
    slug: "jeep-renegade",
    displayName: "Jeep Renegade",
    brand: "Jeep",
    match: ["renegade"],
    family: "jeep-renegade",
  },
  {
    slug: "porsche-cayenne",
    displayName: "Porsche Cayenne",
    brand: "Porsche",
    match: ["cayenne"],
    family: "porsche-cayenne",
    shortDescription: "SUV esportivo de alto desempenho com crescente oferta no mercado de blindados.",
  },
  {
    slug: "byd-song-plus",
    displayName: "BYD Song Plus",
    brand: "BYD",
    match: ["song plus"],
    family: "byd-song",
    shortDescription: "SUV híbrido plug-in da BYD, com demanda crescente por blindagem no Brasil.",
  },
  {
    slug: "byd-seal",
    displayName: "BYD Seal",
    brand: "BYD",
    match: ["seal"],
    family: "byd-seal",
    shortDescription: "Sedan elétrico puro da BYD com autonomia longa e perfil executivo.",
  },
  {
    slug: "gwm-haval-h6",
    displayName: "GWM Haval H6",
    brand: "GWM",
    match: ["haval h6", "haval-h6"],
    family: "gwm-haval",
    shortDescription: "SUV médio da GWM com boa relação custo-benefício e oferta crescente blindado.",
  },
];

// Marcas — agregam vários modelos. Geradas a partir dos modelos.
export const MARCAS: SeoEntry[] = (() => {
  const byBrand = new Map<string, SeoEntry>();
  for (const m of MODELOS) {
    if (byBrand.has(m.brand)) continue;
    byBrand.set(m.brand, {
      slug: slugify(m.brand),
      displayName: m.brand,
      brand: m.brand,
      match: [m.brand.toLowerCase()],
    });
  }
  return Array.from(byBrand.values());
})();

export function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Best /api/listings filter for an SeoEntry — prefers `family` (1:1 with DB
 * model_family) and falls back to comma-separated match terms via `q` (ILIKE
 * on display_title/title). Shared between /buscar, /, and SEO pages so the
 * counter on a card matches what the user sees after clicking it.
 */
export function entryFilter(entry: SeoEntry): { family?: string; q?: string } {
  if (entry.family) {
    const families = Array.isArray(entry.family) ? entry.family : [entry.family];
    return { family: families.join(",") };
  }
  return { q: entry.match.join(",") };
}

export function findModelo(slug: string) {
  return MODELOS.find((m) => m.slug === slug);
}

export function findMarca(slug: string) {
  return MARCAS.find((m) => m.slug === slug);
}

// Mínimo de listings para uma página ser indexada/renderizada.
// Abaixo disso → noindex + 404 (evita thin/doorway pages).
// Mirror this in lib/locations.ts when curating SEO_LOCATIONS.
export const MIN_LISTINGS_TO_INDEX = 5;
